import { useEffect, useState } from 'react';
import { financialConfigApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Notice } from '@verlo/shared/components/ui/notice';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * Settings › Signing in on the till (Oct 2026 sweep). Both off by default, so a PIN alone
 * signs anyone in, as before. An owner or manager can ask for a name before the PIN, and
 * then only let people on today's rota sign in (a manager can let someone else on, and
 * that goes in the audit log). The till reads these from the venue's settings.
 */
const SignInCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { financialConfigApi.getSettings(undefined).then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const cur = { by_name: false, rota: false, ...(cfg.default_pin_sign_in || {}) };
  const set = (patch) => setCfg({ ...cfg, default_pin_sign_in: { ...cur, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const byName = !!cur.by_name;
      const { data } = await financialConfigApi.updateSettings({ default_pin_sign_in: { by_name: byName, rota: byName && !!cur.rota } });
      setCfg(data);
      toast.success('Signing in saved. The tills pick it up within a minute.');
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save how the tills sign in"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="settings-sign-in">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Signing in on the till</CardTitle>
        <CardDescription>How your team signs in to a till. Off, a PIN on its own signs anyone in.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center justify-between gap-3 rounded-md border border-hairline px-3 py-2.5 text-[13.5px] text-ink">
          <span>Tap your name, then your PIN
            <span className="block text-[12px] text-ink-faint">A PIN only works for the person tapped, so a guessed or shared PIN isn't anyone.</span>
          </span>
          <Switch checked={!!cur.by_name} onCheckedChange={(v) => set({ by_name: v, ...(v ? {} : { rota: false }) })} data-testid="settings-signin-by-name" />
        </label>
        <label className={`flex items-center justify-between gap-3 rounded-md border border-hairline px-3 py-2.5 text-[13.5px] ${cur.by_name ? 'text-ink' : 'text-ink-faint'}`}>
          <span>Only people on today's rota
            <span className="block text-[12px] text-ink-faint">Anyone else needs a manager's PIN to be let on for the shift. It's written to the audit log. Needs names on.</span>
          </span>
          <Switch checked={!!cur.by_name && !!cur.rota} disabled={!cur.by_name} onCheckedChange={(v) => set({ rota: v })} data-testid="settings-signin-rota" />
        </label>
        {cur.by_name && cur.rota && (
          <Notice tone="info" word="Rota" title="Keep the rota up to date">
            Someone covering a shift that isn't on the rota will need a manager to let them on.
          </Notice>
        )}
        <div className="flex justify-end">
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="settings-signin-save">
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SignInCard;
